import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
//import type { IDocument } from "../../../shared/services/api/signature/SignatureServices";
import type { IDocument, IEnvelopeData, ISignatario } from "../types/types";
import EnvelopeTabDados from "./tabs/EnvelopeTabDados";
import EnvelopeTabDocumentos from "./tabs/EnvelopeTabDocumentos";
import EnvelopeTabSignatarios from "./tabs/EnvelopeTabSignatarios";

type EnvelopeTabKey = "dados" | "signatarios" | "documentos";

interface Props {
	open: boolean;
	onClose: () => void;
	row?: IEnvelopeData; // linha completa recebida da listagem
	isNew?: boolean; // indica fluxo de criação
	initialTab?: EnvelopeTabKey; // opcional, default "dados"
	onSuccessRefresh?: () => void; // atualizar lista base somente quando houver mudança relevante
}

export default function EnvelopeEditModal({ open, onClose, row, isNew = false, initialTab = "dados", onSuccessRefresh }: Props) {
	const [tab, setTab] = useState<EnvelopeTabKey>(initialTab);
	const [envelope, setEnvelope] = useState<IEnvelopeData | undefined>(row);
	const [signers, setSigners] = useState<ISignatario[]>(() => (row?.signatories ?? []).filter((s) => s.type !== "observer"));
	const [observers, setObservers] = useState<ISignatario[]>(() => (row?.signatories ?? []).filter((s) => s.type === "observer"));
	const [documents, setDocuments] = useState<IDocument[]>(row?.documents ?? []);

	useEffect(() => {
		if (!open) return;

		setTab(initialTab);
		setEnvelope(row);
		setSigners((row?.signatories ?? []).filter((s) => s.type !== "observer"));
		setObservers((row?.signatories ?? []).filter((s) => s.type === "observer"));
		setDocuments(row?.documents ?? []);
	}, [open, row, initialTab]);

	const hasId = Boolean(envelope?.id);
	const isCreating = isNew || !hasId;

	const signersCount = useMemo(() => signers.length, [signers]);
	const docsCount = useMemo(() => documents.length, [documents]);

	const handleEnvelopeSaved = (saved: IEnvelopeData) => {
		setEnvelope(saved);

		if (!hasId && saved.id) {
			onSuccessRefresh?.();
		} else {
			onSuccessRefresh?.();
		}

		setTab("signatarios");
	};

	const handleSignersChange = (nextSigners: ISignatario[], nextObservers: ISignatario[]) => {
		setSigners(nextSigners);
		setObservers(nextObservers);
		onSuccessRefresh?.();
	};

	const handleDocumentsChange = (nextDocs: IDocument[]) => {
		setDocuments(nextDocs);
		onSuccessRefresh?.();
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<Typography variant="h6" fontWeight={500}>
					{isCreating ? "Novo envelope" : "Editar envelope"}
					{envelope?.name ? ` — ${envelope.name}` : ""}
				</Typography>
				<IconButton onClick={onClose} aria-label="Fechar">
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers>
				<Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
					<Tab label="Dados" value="dados" />
					<Tab label={`Signatários (${signersCount})`} value="signatarios" disabled={!hasId} />
					<Tab label={`Documentos (${docsCount})`} value="documentos" disabled={!hasId} />
				</Tabs>

				{/* DADOS */}
				<Box role="tabpanel" hidden={tab !== "dados"}>
					{tab === "dados" && (
						<EnvelopeTabDados
							active
							isNew={isCreating}
							envelope={envelope}
							onEnvelopeSaved={handleEnvelopeSaved}
							signersCount={signersCount}
							documentsCount={docsCount}
						/>
					)}
				</Box>

				{/* SIGNATÁRIOS */}
				<Box role="tabpanel" hidden={tab !== "signatarios"}>
					{tab === "signatarios" && (
						<EnvelopeTabSignatarios
							active
							envelopeId={envelope?.id!}
							signers={signers}
							observers={observers}
							onChange={handleSignersChange}
							envelopeStatus={envelope?.status}
						/>
					)}
				</Box>

				{/* DOCUMENTOS */}
				<Box role="tabpanel" hidden={tab !== "documentos"}>
					{tab === "documentos" && (
						<EnvelopeTabDocumentos
							active
							envelopeId={envelope?.id!} // só renderiza se hasId = true
							documents={documents}
							onChange={handleDocumentsChange} // retorna (nextDocuments)
							envelopeStatus={envelope?.status}
						/>
					)}
				</Box>
			</DialogContent>

			<DialogActions>
				<Stack direction="row" spacing={1}>
					<Button onClick={onClose}>Fechar</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	);
}
